import { type IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { useEffect } from 'react'
import { useParams } from 'react-router'

import { type OnboardingPage } from '../routes'
import Calendly from '../../components/Calendly'
import { PRODUCTS } from '../../constants/products'
import { useQuotation } from '../context'
import { ProductEnum } from '../../generated/graphql'
import { track } from '../../utils/analytics'
import usePartner from '../../utils/usePartner'
import { ProductCard } from '../components/ProductCard'
import { OnboardingTitle } from '../components/OnboardingTitle'
import { useOnboardingWorkflow } from '../workflow'
import { TipsCard } from '../components/TipsCard'

import { Button, Loader } from '@olino/design-system'

const categories: Array<{
  name: string
  as: 'h2' | 'h3'
  description?: string
  products: Array<{
    title: string
    description: string
    icon: IconDefinition
    value: ProductEnum
    disabled: boolean
  }>
}> = [
  {
    name: 'Acheel',
    as: 'h2',
    description: 'Choisissez les produits qui correspondent à votre activité.',
    products: [],
  },
]

const Main = () => {
  const { id } = useParams()
  const { updateQuotation } = useQuotation()
  const { partner, code: partnerCode } = usePartner()
  const { proceed } = useOnboardingWorkflow()

  const handleSelect = (product: ProductEnum) => {
    track({
      event: 'submitted_product',
      quotation_id: id,
    })

    proceed(() =>
      updateQuotation({
        details: {
          products: [product],
        },
      })
    )
  }

  useEffect(() => {
    track({
      event: 'visited_product',
      quotation_id: id,
    })
  }, [id])

  if (partnerCode !== null && partner === null) return <Loader />

  return (
    <>
      <OnboardingTitle.H1>Quel est votre besoin&nbsp;?</OnboardingTitle.H1>

      <section className="space-y-8">
        {categories.map((category) => {
          const productList = partner
            ? category.products.filter(
                (product) =>
                  partner?.products &&
                  partner.products.some(
                    (partnerProduct) =>
                      partnerProduct.product === product.value &&
                      partnerProduct.displayDuringQuotation
                  )
              )
            : category.products

          const isCategoryEmpty = partner && productList.length === 0

          if (isCategoryEmpty) return null

          return (
            <div key={category.name}>
              <div className="grid gap-3 sm:grid-cols-2">
                {productList.map((product) => (
                  <ProductCard
                    key={product.value}
                    onClick={() => handleSelect(product.value)}
                    name={product.title}
                    icon={product.icon}
                    description={product.description}
                    disabled={product.disabled}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </section>

      <div className="mt-7 text-center">
        <span>Vous avez un autre besoin ? </span>
        <Calendly type="popup" reason="cant-find-product">
          {(open) => (
            <Button variant="link" onClick={open}>
              Cliquez ici pour en discuter avec un de nos experts.
            </Button>
          )}
        </Calendly>
      </div>
    </>
  )
}

const Tips = () => {
  return (
    <div className="space-y-3">
      <TipsCard
        title="Notre parcours en ligne"
        content={[
          'Vous pouvez à tout moment contacter un de nos experts pour poser vos questions.',
        ]}
      />
    </div>
  )
}

export default {
  main: <Main />,
  tips: <Tips />,
} satisfies OnboardingPage
